import * as _ from "lodash-es";
import { useContext, useEffect, useRef, useState } from "react";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
import Modal from "react-modal";
import { Link, useNavigate, useLocation } from "react-router-dom";
import DashboardComponentEnum from "../../../models/DashboardComponentEnum";
import DashboardWidgetTypeEnum from "../../../models/DashboardWidgetTypeEnum";
import ILoadDashboardDTO from "../../../models/DTO/ILoadDashboardDTO";
import IOnLoadDashboardDTO from "../../../models/DTO/IOnLoadDashboardDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICacheHealth from "../../../models/ICacheHealth";
import ICatalog from "../../../models/ICatalog";
import IDashboard from "../../../models/IDashboard";
import IDashboardItem from "../../../models/IDashboardItem";
import IOrder from "../../../models/IOrder";
import IOrdersAtStep from "../../../models/IOrdersAtStep";
import SalesTotalTypeEnum from "../../../models/SalesTotalTypeEnum";
import UserTypesEnum from "../../../models/UserTypesEnum";
import IdbService from "../../../services/IdbService";
import UIService from "../../../services/UIService";
import UserService from "../../../services/UserService";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import FullScreenActivityIndicator from "../../shared/FullScreenActivityIndicator/FullScreenActivityIndicator";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import CoopTotalWidget from "../CoopTotalWidget/CoopTotalWidget";
import FeedbackReviewModal from "../FeedbackReviewModal/FeedbackReviewModal";
import { InventoryWidget } from "../InventoryWidget/InventoryWidget";
import { LoginMessage } from "../LoginMessage/LoginMessage";
import { MonthlyOrdersWidget } from "../MonthlyOrdersWidget/MonthlyOrdersWidget";
import MonthlyShipmentsWidget from "../MonthlyShipmentsWidget/MonthlyShipmentsWidget";
import OfflineModeNotReadyModal from "../OfflineModeNotReadyModal/OfflineModeNotReadyModal";
import OrdersAtStepWidget from "../OrdersAtStepWidget/OrdersAtStepWidget";
import RebateTotalWidget from "../RebateTotalWidget/RebateTotalWidget";
import RecentOrdersWidget from "../RecentOrdersWidget/RecentOrdersWidget";
import { SalesTotalByPeriodWidget } from "../SalesTotalByPeriodWidget/SalesTotalByPeriodWidget";
import { SystemMessagesWidget } from "../SystemMessagesWidget/SystemMessagesWidget";
import { YearlySnapshot } from "../YearlySnapshot/YearlySnapshot";
import "./Dashboard.scss";
import { AccountDetailWidget } from "../AccountDetailWidget/AccountDetailWidget";
import { DashboardRemoteUrlWidget } from "../DashboardRemoteUrlWidget/DashboardRemoteUrlWidget";
import { CreateOrderWidget } from "../CreateOrderWidget/CreateOrderWidget";
import IDashboardDownloadDataDTO from "../../../models/DTO/IDashboardDownloadDataDTO";
import IDashboardOnDownloadDataDTO from "../../../models/DTO/IDashboardOnDownloadDataDTO";
import { NewsletterModal } from "../NewsletterModal/NewsletterModal";
import { FreightPolicyWidget } from "../FreightPolicyWidget/FreightPolicyWidget";
import { SalesSummaryWidget } from "../SalesSummaryWidget/SalesSummaryWidget";
import { DueDateCalendarWidget } from "../DueDateCalendarWidget/DueDateCalendarWidget";
import VendorContext from "../../../contexts/VendorContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnLoadUserDTO from "../../../models/DTO/IOnLoadUserDTO";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import ICatalog_Item from "../../../models/ICatalog_Item";

// Define at module level to prevent recreation on every render
const ResponsiveGridLayout = WidthProvider(Responsive);

type Props = {
    isOnline: boolean;
};

export const Dashboard = (props: Props) => {
    const idbService = new IdbService();

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const location = useLocation();
    const history = useNavigate();

    const uiContext = useContext(UiContext);

    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine || true);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [offlineOrdersCount, setOfflineOrdersCount] = useState<number>(0);
    const [offlineVendorsCount, setOfflineVendorsCount] = useState<number>(0);

    const [orderVolume, setOrderVolumn] = useState<any[]>([]);
    const [shipmentVolume, setShipmenVolume] = useState<any[]>([]);
    const [recentOrders, setRecentOrders] = useState<any[]>([]);
    const [systemMessages, setSystemMessages] = useState<any[]>([]);
    const [totalClients, setTotalClients] = useState<number>(0);
    const [totalProjects, setTotalProjects] = useState<number>(0);
    const [totalVendors, setTotalVendors] = useState<number>(0);
    const [totalMonthlyOrders, setTotalMonthlyOrders] = useState<number>(0);
    const [totalMonthlyShipments, setTotalMonthlyShipments] = useState<number>(0);
    const [loadedDashboard, setLoadedDashboard] = useState<IDashboard | null>(null);
    // loadDashboard is invoked from mount-time socket handler closures, so the current
    // selection has to be read through a ref rather than state.
    const loadedDashboardGuidRef = useRef<string | null>(null);
    const [onLoadDashboardDto, setOnLoadDashboardDto] = useState<IOnLoadDashboardDTO | null>(null);
    const [dashboards, setDashboards] = useState<IDashboard[]>([]);
    const [lastFiveStock, setLastFiveStock] = useState<Partial<ICatalog_Item>[]>([]);
    const [coopTotal, setCoopTotal] = useState<number>(0);
    const [rebateTotal, setRebateTotal] = useState<number>(0);
    const [dailySales, setDailySales] = useState<number>(0);
    const [monthlySales, setMonthlySales] = useState<number>(0);
    const [weeklySales, setWeeklySales] = useState<number>(0);
    const [yearlySales, setYearlySales] = useState<number>(0);
    const [ordersAtStep, setOrdersAtStep] = useState<IOrdersAtStep>({});
    const [dashboardWidgets, setDashboardWidgets] = useState<any>(null);
    const [cacheHealth, setCacheHealth] = useState<ICacheHealth | undefined>(undefined);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState<number>(0);
    const [alertOrders, setAlertOrders] = useState<IOrder[]>([]);

    const modalStyle = {
        content: {
            top: "10px",
            left: "10px",
            right: "10px",
            bottom: "10px",
            color: "#FFFFFF",
            backgroundColor: "#313439",
            border: "1px solid #44464a",
        },
        overlay: {
            zIndex: 100,
        },
    };

    useEffect(() => {
        if (userModel.isSupplierUser && _.isNil(userModel.release_newsletter)) {
            showReleaseNewsletterModal();
        } else {
            setModalComponent(null);
            setModalIsOpen(false);
        }
    }, [userModel]);

    useEffect(() => {
        loadedDashboardGuidRef.current = loadedDashboard?.guid ?? null;

        if (loadedDashboard) {
            const _dashboardWidgets = getDashboardWidgets();

            setDashboardWidgets(_dashboardWidgets);
            setIsLoading(false);
            uiContext.setActivityIndicator(false);
        }
    }, [loadedDashboard]);

    const setupLoadedDashboard = () => {
        const hasDefaultUserDashboard = false;
        let dashboard: IDashboard | undefined;

        // keep the user's current selection across refetches; the refetched array holds new
        // object references, so setting the match rebuilds the widgets with the fresh data.
        if (loadedDashboardGuidRef.current) {
            dashboard = dashboards.find((_dashboard) => {
                return _dashboard.guid === loadedDashboardGuidRef.current;
            });
        }

        // otherwise find the default user dashboard
        if (!dashboard) {
            dashboard = dashboards.find((_dashboard) => {
                return _dashboard.id === userModel.supplierUserToken?.default_user_dashboard_id;
            });
        }

        // console.log("user dash: ", dashboard);

        if (!dashboard) {
            dashboard = dashboards.find((_dashboard) => {
                return _dashboard.id === supplierModel.default_dashboard_id;
            });

            // console.log("default dash: ", dashboard);

            if (!userModel.isSupplierUser) {
                if (supplierModel.default_vendors_dashboard_id) {
                    dashboard = dashboards.find((_dashboard) => {
                        return _dashboard.id === supplierModel.default_vendors_dashboard_id;
                    });

                    // console.log("default vendors dash: ", dashboard);
                }
            }
        }

        if (dashboard) {
            setLoadedDashboard(dashboard);
        }
    };

    useEffect(() => {
        setupLoadedDashboard();
    }, [dashboards]);

    const getDefaultDashboardConfig = () => {
        return [
            {
                item: {
                    h: 4,
                    i: "monthly_orders-d2f2e83d-3d97-484a-8d59-21fa7c174179",
                    w: 3,
                    x: 0,
                    y: 0,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: false,
                },
                uuid: "d2f2e83d-3d97-484a-8d59-21fa7c174179",
                title: "monthly_orders",
                layout_index: "monthly_orders-d2f2e83d-3d97-484a-8d59-21fa7c174179",
                allowMultiple: false,
            },
            {
                item: {
                    h: 4,
                    i: "monthly_shipments-f01d5d1b-6c6f-40ea-91cb-4614ef699749",
                    w: 3,
                    x: 3,
                    y: 0,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: false,
                },
                uuid: "f01d5d1b-6c6f-40ea-91cb-4614ef699749",
                title: "monthly_shipments",
                layout_index: "monthly_shipments-f01d5d1b-6c6f-40ea-91cb-4614ef699749",
                allowMultiple: false,
            },
            {
                item: {
                    h: 6,
                    i: "last_five_inventory-a2d7dc1a-9f36-4c31-9c7f-735e4b18e4b1",
                    w: 6,
                    x: 6,
                    y: 0,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: false,
                },
                uuid: "a2d7dc1a-9f36-4c31-9c7f-735e4b18e4b1",
                title: "last_five_inventory",
                layout_index: "last_five_inventory-a2d7dc1a-9f36-4c31-9c7f-735e4b18e4b1",
                allowMultiple: false,
            },
            {
                item: {
                    h: 2,
                    i: "system_messages-fcf91f81-fcb0-4771-afa0-615137abe188",
                    w: 6,
                    x: 0,
                    y: 4,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: true,
                    resizeHandles: ["se"],
                },
                uuid: "fcf91f81-fcb0-4771-afa0-615137abe188",
                title: "system_messages",
                layout_index: "system_messages-fcf91f81-fcb0-4771-afa0-615137abe188",
                allowMultiple: false,
                resizeHandles: ["se"],
            },
            {
                item: {
                    h: 6,
                    i: "recent_orders",
                    w: 6,
                    x: 0,
                    y: 6,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: false,
                },
                uuid: "6cf98b4f-42ba-4d45-b700-590b0ffdb012",
                title: "recent_orders",
                layout_index: "recent_orders-6cf98b4f-42ba-4d45-b700-590b0ffdb012",
                allowMultiple: false,
            },
            {
                item: {
                    h: 6,
                    i: "last_year_volume",
                    w: 6,
                    x: 6,
                    y: 6,
                    moved: false,
                    static: false,
                    isDraggable: true,
                    isResizable: false,
                },
                uuid: "bd7f7a72-4135-4243-adc2-b70d35f0b3d0",
                title: "last_year_volume",
                layout_index: "last_year_volume-bd7f7a72-4135-4243-adc2-b70d35f0b3d0",
                allowMultiple: false,
            },
        ];
    };

    useEffect(() => {
        // console.log("onLoadDashboardDto", onLoadDashboardDto);
        if (onLoadDashboardDto) {
            const processData = async () => {
                const dashboardData = await UserService.transformDashboardData(onLoadDashboardDto);

                setOrderVolumn(dashboardData.orderVolume);
                setShipmenVolume(dashboardData.shipmentVolume);
                setRecentOrders(dashboardData.recentOrders);
                setSystemMessages(dashboardData.systemMessages);
                setTotalClients(dashboardData.totalClients);
                setTotalProjects(dashboardData.totalProjects);
                setTotalVendors(dashboardData.totalVendors);
                setTotalMonthlyOrders(dashboardData.totalMonthlyOrders);
                setTotalMonthlyShipments(dashboardData.totalMonthlyShipments);
                setDashboards(dashboardData.dashboards.length ? dashboardData.dashboards : []);
                setLastFiveStock(dashboardData.lastFiveStock);
                setCoopTotal(dashboardData.coopTotal);
                setRebateTotal(dashboardData.rebateTotal);
                setDailySales(dashboardData.dailySales);
                setMonthlySales(dashboardData.monthlySales);
                setWeeklySales(dashboardData.weeklySales);
                setYearlySales(dashboardData.yearlySales);
                setOrdersAtStep(dashboardData.ordersAtStep);
                setAlertOrders(dashboardData.alertOrders);

                if (!dashboardData.dashboards?.length) {
                    setIsLoading(false);
                    uiContext.setActivityIndicator(false);
                    setLoadedDashboard({
                        config: getDefaultDashboardConfig(),
                    } as unknown as IDashboard);
                } else {
                    if (dashboardData.dashboards.length === 1) {
                        setLoadedDashboard(dashboardData.dashboards[0]);
                    } else {
                        // we need to find the default dashboard and set it.
                    }

                    setIsLoading(false);
                    uiContext.setActivityIndicator(false);
                }
            };

            processData();
        }
    }, [onLoadDashboardDto]);

    const onLoadDashboard = async (data: IOnLoadDashboardDTO) => {
        setOnLoadDashboardDto(data);
    };

    const widgetDataUpdated = () => {
        loadDashboard();
    };

    const onConnected = () => {
        setupDashboard();
    };

    const onDownloadData = (data: IDashboardOnDownloadDataDTO) => {
        UIService.downloadDataFile(data.buffer, "Dashboard Data File.xlsx", "application/vnd.ms-excel");
    };

    const onLoadUser = (data: IOnLoadUserDTO) => {
        refreshListener();
        setModalComponent(null);
        setModalIsOpen(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.AppOnLoadUser, fn: onLoadUser },
        { endpoint: EndpointsEnum.DashboardOnLoadDashboard, fn: onLoadDashboard },
        { endpoint: EndpointsEnum.Connected, fn: onConnected },
        { endpoint: EndpointsEnum.DashboardOnDownloadData, fn: onDownloadData },
        { endpoint: EndpointsEnum.DashboardWidgetDataUpdated, fn: widgetDataUpdated },
    ];

    // useEffect(() => {
    //     setupDashboard();
    // }, [uiContext.isLightMode]);

    useEffect(() => {
        document.title = `Orderwerks >> Dashboard`;

        window.addEventListener("online", setOnlineStatus);
        window.addEventListener("offline", setOnlineStatus);

        // UIService.isLightMode$()
        //     .pipe(takeUntil(unsubscribe$))
        //     .subscribe((isLightMode) => setupDashboard());

        // const _offlineMode$ = UIService.offlineModeSubject$().subscribe((_isOnline) => {
        //     const forceOnline = UserService.getForceOnline();
        //
        //     if (forceOnline) {
        //         _isOnline = true;
        //     }
        //
        //     setIsOnline(_isOnline);
        // });

        try {
            if (userModel && isLoading) {
                // idbService.getCacheHealth().then((_cacheHealth) => {
                //     setCacheHealth(_cacheHealth);
                //     setOfflineMode$(_offlineMode$);
                // });
            }
        } catch (err) {
            // console.log(err);
            // UserService.performLogout();
        }

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        loadDashboard();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    // useEffect(() => {
    //     if (dashboards.length) {
    //         const _dashboardWidgets = getDashboardWidgets();
    //
    //         setDashboardWidgets(_dashboardWidgets);
    //         setIsLoading(false);
    //         uiContext.setActivityIndicator(false);
    //     }
    // }, [dashboards]);

    const setupDashboard = () => {
        if (isOnline) {
            // getDashboardData();
            loadDashboard();
        } else {
            setIsLoading(true);
            getOfflineDashboardData();
        }
    };

    const getOfflineDashboardData = async () => {
        const offlineOrders = await idbService.getOrders();
        const offlineVendors = await idbService.getOfflineVendors();

        setIsLoading(false);
        setOfflineOrdersCount(offlineOrders.length);
        setOfflineVendorsCount(offlineVendors.length);
    };

    const setOnlineStatus = () => {
        let _isOnline = navigator.onLine;

        const forceOnline = UserService.getForceOnline();

        if (forceOnline) {
            _isOnline = true;
        }

        setIsOnline(_isOnline);
    };

    const loadDashboard = (dashboardGuid?: string) => {
        if (!userModel) {
            return;
        }

        const payload: ILoadDashboardDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                callingFn: "Dashboard.loadDashboard",
            },
        };

        const activeDashboardGuid = dashboardGuid ?? loadedDashboardGuidRef.current;

        if (activeDashboardGuid) {
            payload.dashboardGuid = activeDashboardGuid;
        }

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.DashboardLoadDashboard, payload);
    };

    const getDashboardWidgets = () => {
        let _dashboardWidgets: any = null;

        if (loadedDashboard?.config) {
            _dashboardWidgets = loadedDashboard.config.map((widget, index) => {
                const component = getWidget(widget, index, loadedDashboard);

                return component;
            });
        }

        return _dashboardWidgets;
    };

    const getRecentOrdersWidget = () => {
        return (
            <RecentOrdersWidget
                userModel={userModel}
                supplierModel={supplierModel}
                vendorModel={vendorModel}
                recentOrders={recentOrders}
                socket={socket}
                history={history}
            />
        );
    };

    const showReviewModal = (catalogModel?: ICatalog) => {
        const component = (
            <FeedbackReviewModal userModel={userModel} supplierModel={supplierModel} socket={socket} closeModal={closeModal} />
        );

        uiContext.showModal(true);
        uiContext.setModalComponent(component);
    };

    const refreshListener = () => {
        socket.removeListener(EndpointsEnum.DashboardOnLoadDashboard, onLoadDashboard);
        socket.on(EndpointsEnum.DashboardOnLoadDashboard, onLoadDashboard);
    };

    const closeModal = (refreshData?: boolean) => {
        setModalComponent(null);
        setModalIsOpen(false);

        uiContext.setModalComponent(null);
        uiContext.showModal(false);

        loadDashboard();
    };

    const configureDashboard = () => {
        history("/dashboard/config");
    };

    const changeSelectedDashboard = (dashboard: IDashboard) => {
        loadedDashboardGuidRef.current = dashboard.guid;
        setLoadedDashboard(dashboard);
        loadDashboard(dashboard.guid);
    };

    const getDashboardHeader = () => {
        let isOfflineReady = false;

        if (cacheHealth && cacheHealth.isOfflineReady) {
            isOfflineReady = true;
        }

        let hideFeedback = true;

        if (userModel && supplierModel) {
            if (!userModel.has_reviewed) {
                if (!userModel.isSupplierUser && !supplierModel.hide_vendor_feedback_help) {
                    hideFeedback = false;
                }

                if (userModel.isSupplierUser) {
                    hideFeedback = false;
                }
            }
        }

        let loginMessageDetail: any = null;

        if (supplierModel) {
            loginMessageDetail = (
                <div className={"col-12 mt-2 mb-2"}>
                    <LoginMessage loginMessageDetail={supplierModel.login_message_detail} showPreviewBox={false} />
                </div>
            );
        }

        return (
            <div className={"row"}>
                <div className={"col-12 d-flex align-items-center"}>
                    <div className={"d-flex me-auto align-items-center"}>
                        <OwTitle className={"me-4"} tag={"h2"} title={"dashboard"} leftIconName={"fad fa-tachometer-alt-fastest"} />
                        {userModel.isSupplierUser && userModel.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN ? (
                            <div className={"d-none d-md-inline-flex"}>
                                <OwButton leftIconClass={"fad fa-cog"} title={"config"} fn={configureDashboard} />
                                <div className={"ms-2"}>
                                    <OwSelectOption
                                        fn={changeSelectedDashboard}
                                        titleProp={"name"}
                                        valueProp={"guid"}
                                        name={"dashboard"}
                                        options={dashboards}
                                        selectedOption={loadedDashboard}
                                        selectedOptionProp={"guid"}
                                        emitObject={true}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={"d-flex flex-column"}>
                        {/*{userModel && userModel.isSupplierUser ? (*/}
                        {/*    <Link className={"text-small " + (isOfflineReady ? "text-success" : "text-danger")} to={"/cache"}>*/}
                        {/*        <OwLabel*/}
                        {/*            leftIconClass={"fad fa-share-square"}*/}
                        {/*            noBottomMargin={true}*/}
                        {/*            className={"text-underline pointer"}*/}
                        {/*            title={isOfflineReady ? "offline_ready" : "not_offline_ready"}*/}
                        {/*        />*/}
                        {/*    </Link>*/}
                        {/*) : null}*/}
                        {hideFeedback ? null : (
                            <div className={"leave-feedback"}>
                                <OwLabel
                                    leftIconClass={"fas fa-thumbs-up"}
                                    className={"text-small"}
                                    title={"leave_feedback"}
                                    fn={() => {
                                        showReviewModal();
                                    }}
                                />
                            </div>
                        )}
                        {supplierModel.trial_exp_date ? (
                            <div className="text-small text-warning">
                                <OwLabel
                                    leftIconClass={"fad fa-hourglass-start"}
                                    title={"trial_exp_date"}
                                    postText={":"}
                                    className={"me-2"}
                                />
                                {UserService.parseDate(supplierModel.trial_exp_date)}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    const getWidget = (widget: IDashboardItem, index: number, dashboard: IDashboard) => {
        let component: any = null;

        const isMainViewLight = UIService.isMainViewLight();

        if (userModel.isVendorSourceUser) {
            return null;
        }

        switch (widget.title) {
            case DashboardWidgetTypeEnum.MONTHLY_ORDERS:
                component = (
                    <MonthlyOrdersWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        totalMonthlyOrders={totalMonthlyOrders}
                        inEditor={false}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.MONTHLY_SHIPMENTS:
                component = (
                    <MonthlyShipmentsWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        totalMonthlyShipments={totalMonthlyShipments}
                        inEditor={false}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.LAST_YEAR_VOLUME:
                component = (
                    <YearlySnapshot
                        key={widget.layout_index}
                        data-grid={widget.item}
                        shipmentVolume={shipmentVolume}
                        orderVolume={orderVolume}
                        inEditor={false}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.RECENT_ORDERS:
                component = (
                    <RecentOrdersWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        recentOrders={recentOrders}
                        inEditor={false}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        socket={socket}
                        history={history}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.SALES_SUMMARY:
                component = (
                    <SalesSummaryWidget
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        inEditor={false}
                        widget={widget}
                        dashboard={dashboard}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.DUE_DATE_CALENDAR:
                component = (
                    <DueDateCalendarWidget
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        inEditor={false}
                        widget={widget}
                        dashboard={dashboard}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.CREATE_ORDER:
                component = (
                    <CreateOrderWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        socket={socket}
                        notifyFn={uiContext.notifyFn}
                        inEditor={false}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.FREIGHT_POLICY:
                component = <FreightPolicyWidget />;
                break;

            case DashboardWidgetTypeEnum.LAST_FIVE_INVENTORY:
                component = (
                    <InventoryWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        lastFiveStock={lastFiveStock}
                        inEditor={false}
                        isMainViewLight={isMainViewLight}
                    />
                );
                break;

            case DashboardWidgetTypeEnum.ORDERS_AT_STEP:
                {
                    const _ordersAtStep = ordersAtStep[widget.widget_data] || {};

                    const count = _ordersAtStep.count || 0;
                    const stepName = _ordersAtStep.stepName || "";
                    component = (
                        <OrdersAtStepWidget
                            key={widget.layout_index}
                            data-grid={widget.item}
                            socket={socket}
                            notifyFn={uiContext.notifyFn}
                            inEditor={false}
                            widget={widget}
                            selectedStepDetail={{ step: widget.widget_data, count, stepName }}
                        />
                    );
                }
                break;

            case DashboardWidgetTypeEnum.ACCOUNT_DETAIL_LOOKUP:
                component = <AccountDetailWidget socket={socket} notifyFn={uiContext.notifyFn} inEditor={false} />;
                break;

            case DashboardWidgetTypeEnum.REMOTE_URL:
                component = <DashboardRemoteUrlWidget socket={socket} notifyFn={uiContext.notifyFn} inEditor={false} widget={widget} />;
                break;

            case DashboardWidgetTypeEnum.DAILY_SALES:
            case DashboardWidgetTypeEnum.WEEKLY_SALES:
            case DashboardWidgetTypeEnum.MONTHLY_SALES:
            case DashboardWidgetTypeEnum.YEARLY_SALES:
                {
                    let type: SalesTotalTypeEnum = SalesTotalTypeEnum.DAILY;

                    let amount = dailySales;

                    if (widget.title === DashboardWidgetTypeEnum.WEEKLY_SALES) {
                        type = SalesTotalTypeEnum.WEEKLY;
                        amount = weeklySales;
                    } else if (widget.title === DashboardWidgetTypeEnum.MONTHLY_SALES) {
                        type = SalesTotalTypeEnum.MONTHLY;
                        amount = monthlySales;
                    } else if (widget.title === DashboardWidgetTypeEnum.YEARLY_SALES) {
                        type = SalesTotalTypeEnum.YEARLY;
                        amount = yearlySales;
                    }

                    component = (
                        <SalesTotalByPeriodWidget
                            key={widget.layout_index}
                            data-grid={widget.item}
                            type={type}
                            amount={amount}
                            inEditor={false}
                        />
                    );
                }
                break;

            case DashboardWidgetTypeEnum.SYSTEM_MESSAGES:
                component = (
                    <SystemMessagesWidget
                        key={widget.layout_index}
                        data-grid={widget.item}
                        systemMessages={systemMessages}
                        inEditor={false}
                    />
                );
                break;
        }

        // return component;

        const dataGridItem = widget.item;

        dataGridItem.static = true;
        dataGridItem.isDraggable = false;

        return (
            <div key={`${widget.layout_index}`} data-grid={dataGridItem}>
                {component}
            </div>
        );
    };

    const getGridLayouts = (): Layouts => {
        const lg: Layout = { i: "a", x: 0, y: 0, w: 1, h: 5 };
        const md: Layout = { i: "b", x: 1, y: 0, w: 1, h: 2 };
        const sm: Layout = { i: "c", x: 6, y: 0, w: 1, h: 2 };

        const layouts: Layouts = {
            lg: [lg],
            md: [md],
            sm: [sm],
        };

        return layouts;
    };

    const downloadData = () => {
        const payload: IDashboardDownloadDataDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
        };

        socket.emit(EndpointsEnum.DashboardDownloadData, payload);
    };

    const downloadDashboardDataCsv = () => {
        downloadData();
    };

    const getDashboardSetup = () => {
        const layouts = getGridLayouts();

        // compile the dashboard.
        return (
            <div className="dashboard mb-3">
                <FullScreenActivityIndicator showActivityIndicator={isLoading} />
                {getDashboardHeader()}

                {uiContext.showHiddenFeatures ? (
                    <div className={"mb-2"}>
                        <OwButton title={"download_data"} fn={downloadDashboardDataCsv} />
                    </div>
                ) : null}

                <div className={"dashboard grid-wrapper"}>
                    <ResponsiveGridLayout
                        className="layout"
                        layouts={layouts}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
                        rowHeight={50}
                        isBounded={false}
                        isDroppable={false}
                        containerPadding={[0, 0]}>
                        {dashboardWidgets}
                    </ResponsiveGridLayout>
                </div>

                <div>
                    <Modal
                        isOpen={modalIsOpen}
                        contentLabel="Orderwerks"
                        shouldCloseOnOverlayClick={false}
                        className={"modalContentClass"}
                        overlayClassName={"modalOverlayClass"}
                        style={modalStyle}>
                        {modalComponent}
                    </Modal>
                </div>
            </div>
        );
    };

    const showReleaseNewsletterModal = () => {
        const component = (
            <NewsletterModal closeModalFn={closeModal} socket={socket} notifyFn={uiContext.notifyFn} refreshListener={refreshListener} />
        );

        uiContext.setModalComponent(component);
        uiContext.showModal(true);
    };

    /**
     * Setup the Online Dashboard, either with a pre-configured dashboard if none are setup, or with the users custom dashboard.
     */
    const getOnlineDashboard = () => {
        if (isLoading) {
            return <FullScreenActivityIndicator showActivityIndicator={isLoading} />;
        } else {
            // if (supplierModel.default_dashboard_id) {
            if (dashboards.length || dashboardWidgets) {
                return getDashboardSetup();
            } else {
                return (
                    <div className={"d-flex justify-content-center align-items-center"}>
                        <ActivityIndicator active={true} />
                        <OwLabel className={"ms-2"} title={"loading"} noBottomMargin={true} />
                    </div>
                );
            }
            // } else {
            //     return getDefaultDashboard();
            // }
        }
    };

    const getOfflineDashboard = () => {
        const totalOfflineOrders = offlineOrdersCount;
        const totalOfflineVendors = offlineVendorsCount;

        return (
            <div className="dashboard mb-3">
                <div className="row">
                    <div className={"col-12"}>
                        <OwTitle tag={"h2"} title={"dashboard"} leftIconName={"fad fa-tachometer-alt-fastest"} />
                    </div>
                </div>
                <div className="row">
                    <div className={"col-12 col-md-6 row ms-0 me-0"}>
                        <div className="col-12 col-sm-6 col-md-6 mb-2 mb-sm-0 ms-0 ps-0 me-0 pe-0 pe-md-2">
                            <div className="col-12 widget">
                                <OwTitle tag={"h4"} title={"offline_orders"} />
                                <div className="faded-orange number">{totalOfflineOrders}</div>
                                <Link to="/orders">
                                    <OwLabel title={"view"} className={"pointer"} />
                                </Link>
                            </div>
                        </div>

                        <OfflineModeNotReadyModal showModal={(cacheHealth && !cacheHealth.isOfflineReady) || false} />
                    </div>
                </div>
            </div>
        );
    };

    const getUserDashboard = () => {
        switch (supplierModel.dashboard_component) {
            case DashboardComponentEnum.LMC_Dashboard:
                return null;
            default:
                return getOnlineDashboard();
        }
    };

    if (!userModel || !vendorModel || !supplierModel) {
        return null;
    }

    return <>{isOnline ? getUserDashboard() : getOfflineDashboard()}</>;
};
